import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  TrendingUp,
  Users,
  FileCheck,
  AlertTriangle,
  Clock,
  ArrowRight,
  UserCheck,
  Wallet,
  Layers,
  Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { useBrokerData } from '../../context/BrokerDataContext';
import { getBrokerFromPath } from '../../config/brokerConfig';
import './AdminDashboard.css';

function AdminDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const brokerId = getBrokerFromPath(location.pathname);

  // Use broker-specific data from BrokerDataContext
  const {
    transactions,
    pendingRegistrations,
    isLoading,
    totalVolume
  } = useBrokerData();

  // Check if data is loaded
  const isDataReady = !isLoading;

  // Calculate statistics
  const totalTransactions = transactions.length;
  const pendingReview = transactions.filter(t => t.status === 'pending_review').length;
  const pendingAccounts = pendingRegistrations?.length || 0;

  // Use totalVolume from context (calculated from all transactions)
  const totalAmount = totalVolume;

  const stats = [
    {
      title: 'Total Volume',
      value: `PKR ${totalAmount.toLocaleString()}`,
      icon: Wallet,
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      iconBg: 'rgba(16, 185, 129, 0.15)',
      iconColor: '#10b981',
    },
    {
      title: 'Total Transactions',
      value: totalTransactions,
      icon: Layers,
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      iconBg: 'rgba(59, 130, 246, 0.15)',
      iconColor: '#3b82f6',
    },
    {
      title: 'Pending Review',
      value: pendingReview,
      change: pendingReview > 0 ? 'Requires attention' : 'All clear',
      changeType: pendingReview > 0 ? 'warning' : 'positive',
      icon: Clock,
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      iconBg: 'rgba(245, 158, 11, 0.15)',
      iconColor: '#f59e0b',
    },
  ];

  const recentTransactions = [...transactions]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 5);

  const getStatusBadge = (status, verificationStatus) => {
    if (verificationStatus === 'mismatched') {
      return <span className="status-badge warning">Mismatched</span>;
    }
    switch (status) {
      case 'pending_review':
        return <span className="status-badge pending">Pending</span>;
      case 'completed':
        return <span className="status-badge success">Completed</span>;
      case 'rejected':
        return <span className="status-badge danger">Rejected</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  // Show loading state - spinner shows until data is actually loaded
  if (!isDataReady) {
    return (
      <div className="admin-dashboard">
        <div className="loading-container">
          <Loader2 size={48} className="spinning" />
          <h3>Loading Dashboard...</h3>
          <p>Fetching your data</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      {/* Page Header - Matching other admin pages */}
      <div className="page-header">
        <div className="header-content">
          <h1>Dashboard</h1>
          <p>Welcome back! Here's what's happening with your transactions today.</p>
        </div>
        <Button
          variant="primary"
          icon={ArrowRight}
          iconPosition="right"
          onClick={() => navigate(`/${brokerId}/admin/transactions`)}
        >
          View All Transactions
        </Button>
      </div>

      {/* Stats Grid */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className={`stat-card ${stat.changeType}`}>
            <div className="stat-icon" style={{ color: stat.iconColor }}>
              <stat.icon size={26} strokeWidth={1.5} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stat.value}</span>
              <span className="stat-label">{stat.title}</span>
              <div className={`stat-change ${stat.changeType}`}>
                {stat.changeType === 'positive' && <TrendingUp size={12} />}
                {stat.changeType === 'warning' && <AlertTriangle size={12} />}
                <span>{stat.change}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid">
        {/* Recent Transactions */}
        <Card className="transactions-card">
          <CardHeader>
            <CardTitle>Recent Transactions</CardTitle>
            <Button
              variant="ghost"
              size="small"
              onClick={() => navigate(`/${brokerId}/admin/transactions`)}
            >
              View All
            </Button>
          </CardHeader>
          <CardContent>
            <div className="transactions-table">
              <div className="table-header">
                <span>Reference</span>
                <span>User</span>
                <span>Amount</span>
                <span>Status</span>
              </div>
              {recentTransactions.map(transaction => (
                <div key={transaction.id} className="table-row">
                  <span className="ref-number">{transaction.referenceNumber}</span>
                  <span className="user-name">{transaction.userName}</span>
                  <span className="amount">PKR {(transaction.amount || 0).toLocaleString()}</span>
                  {getStatusBadge(transaction.status, transaction.verificationStatus)}
                </div>
              ))}
              {recentTransactions.length === 0 && (
                <div className="empty-state">
                  <p>No transactions yet</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions - Aligned with third stat card */}
        <Card className="actions-card">
          <CardHeader>
            <CardTitle>Quick Actions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="quick-actions">
              <button
                className="action-btn"
                onClick={() => navigate(`/${brokerId}/admin/transactions`)}
              >
                <FileCheck size={20} />
                <span>Review Transactions</span>
                {pendingReview > 0 && (
                  <span className="action-badge">{pendingReview}</span>
                )}
              </button>
              <button
                className="action-btn highlight"
                onClick={() => navigate(`/${brokerId}/admin/account-verification`)}
              >
                <UserCheck size={20} />
                <span>Verify Accounts</span>
                {pendingAccounts > 0 && (
                  <span className="action-badge warning">{pendingAccounts}</span>
                )}
              </button>
              <button
                className="action-btn"
                onClick={() => navigate(`/${brokerId}/admin/users`)}
              >
                <Users size={20} />
                <span>Manage Users</span>
              </button>
              <button
                className="action-btn"
                onClick={() => navigate(`/${brokerId}/admin/reports`)}
              >
                <TrendingUp size={20} />
                <span>View Reports</span>
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default AdminDashboard;
